import 'reflect-metadata';
import type { ClientMetaProvider, ClientMetaPayload } from './ClientMetaProvider';
import ConfigService from './config/ConfigService';
import type Engine from './engine';
import type { IntegrationsService } from './engine/IntegrationsService';
import type NetworkTransportPolicyManager from './engine/network/NetworkTransportPolicyManager';
import type Peer from './engine/Peer';
import type { TransportsWebRTCStats } from './engine/WebRTCStats/types';
import EnhancedEventEmitter from './EnhancedEventEmitter';
import OperationError from './errors/OperationError';
import { container } from './inversify.config';
import { TOKEN } from './inversify.tokens';
import type { ClientParams } from './types/client';
import type {
  AvailableMediaDevices,
  CreateCameraVideoTrackOptions,
  CreateCustomAudioOptions,
  CreateCustomVideoOptions,
  CreateMicrophoneAudioTrackOptions,
  CreateScreenMediaOptions,
  JoinChannelParams,
  Role,
  TransportsStateInfo,
} from './types/common';
import type { ClientObserverEvents } from './types/engine';
import type { AudioTrack, Track, VideoTrack } from './types/media';
import type { LDTransport } from './types/mediasoup';
import type { IceTransportPolicy } from './engine/signalingApiClient/api';
import type { ScoresCalculator } from './qoe/ScoresCalculator';

class Client {
  readonly #engine: Engine;

  readonly #networkTransportPolicyManager: NetworkTransportPolicyManager;

  readonly #clientMetaProvider: ClientMetaProvider;

  private readonly clientEventEmitter: EnhancedEventEmitter;

  readonly canUseNoiseSuppression = true;

  constructor(params: ClientParams) {
    const { clientEventEmitter, internalEventEmitter } = params;

    container
      .bind<ConfigService>(TOKEN.Config)
      .toDynamicValue(() => new ConfigService(params))
      .inSingletonScope();

    container
      .bind<EnhancedEventEmitter>(TOKEN.InternalEventEmitter)
      .toDynamicValue(() => internalEventEmitter ?? new EnhancedEventEmitter())
      .inSingletonScope();

    container
      .bind<EnhancedEventEmitter>(TOKEN.ClientEventEmitter)
      .toDynamicValue(() => clientEventEmitter ?? new EnhancedEventEmitter())
      .inSingletonScope();

    container.get<IntegrationsService>(TOKEN.IntegrationsService); // instantiate service

    this.clientEventEmitter = container.get<EnhancedEventEmitter>(TOKEN.ClientEventEmitter);

    this.#engine = container.get<Engine>(TOKEN.Engine);
    this.#networkTransportPolicyManager = container
      .get<NetworkTransportPolicyManager>(TOKEN.NetworkTransportPolicyManager);
    this.#clientMetaProvider = container.get<ClientMetaProvider>(TOKEN.ClientMetaProvider);
  }

  get meta(): ClientMetaPayload {
    return this.#clientMetaProvider.meta;
  }

  get observer(): EnhancedEventEmitter<ClientObserverEvents> {
    return this.clientEventEmitter;
  }

  get id(): string | undefined {
    return this.#engine.myPeerId;
  }

  get peers(): Peer[] {
    return this.#engine.getPeers();
  }

  get availableVideoDevices(): MediaDeviceInfo[] {
    return this.#engine.system.availableVideoDevices;
  }

  get availableAudioDevices(): MediaDeviceInfo[] {
    return this.#engine.system.availableAudioDevices;
  }

  get effectsSDKInitialized(): boolean {
    return this.#engine.media.effectsSDKInitialized;
  }

  get scoresCalculator(): ScoresCalculator {
    return this.#engine.scoresCalculator;
  }

  async detectDevices(force?: boolean): Promise<AvailableMediaDevices> {
    try {
      await this.#engine.system.detectDevices(force);
    } catch (error) {
      throw new OperationError('detect_devices_failed', error);
    }

    return {
      video: this.#engine.system.availableVideoDevices,
      audio: this.#engine.system.availableAudioDevices,
    };
  }

  async initEffectsSDK(): Promise<boolean> {
    try {
      return await this.#engine.media.initEffectsSDK();
    } catch (error) {
      throw new OperationError('init_effects_sdk_failed', error);
    }
  }

  async join(params: JoinChannelParams): Promise<void> {
    await this.#engine.join(params);
  }

  async leave(keepTracks?: boolean): Promise<void> {
    await this.#engine.release(keepTracks);
  }

  async confirmActivity(): Promise<void> {
    await this.#engine.confirmActivity();
  }

  async createCameraVideoTrack(options?: CreateCameraVideoTrackOptions): Promise<VideoTrack> {
    return await this.#engine.createCameraVideoTrack(options);
  }

  async createMicrophoneAudioTrack(
    options?: CreateMicrophoneAudioTrackOptions,
  ): Promise<AudioTrack> {
    return await this.#engine.createMicrophoneAudioTrack(options);
  }

  async createScreenMediaTracks(options?: CreateScreenMediaOptions): Promise<Track[]> {
    return await this.#engine.createScreenMediaTracks(options);
  }

  createCustomAudioTrack(options: CreateCustomAudioOptions): Track {
    return this.#engine.createCustomAudioTrack(options);
  }

  createCustomVideoTrack(options: CreateCustomVideoOptions): Track {
    return this.#engine.createCustomVideoTrack(options);
  }

  async deleteTrack(track: Track): Promise<void> {
    await this.#engine.deleteTrack(track);
  }

  async loadPeers(role?: Role): Promise<void> {
    await this.#engine.loadPeers(role);
  }

  requestChannelStateSync(): void {
    this.#engine.requestChannelStateSync();
  }

  async updateAppData(appData: Record<string, unknown>): Promise<void> {
    await this.#engine.updateAppData(appData);
  }

  public async getTransportsInfo(): Promise<TransportsStateInfo> {
    try {
      return await this.#engine.network.getTransportsStateInfo();
    } catch (error) {
      throw new OperationError('get_transports_info_failed', error);
    }
  }

  public setPreferRelay(value: boolean): void {
    this.#engine.network.setPreferRelay(value);
  }

  public getPreferRelay(): boolean {
    return this.#engine.network.getPreferRelay();
  }

  public async changeIceTransportPolicy(policy: IceTransportPolicy): Promise<void> {
    try {
      await this.#networkTransportPolicyManager.changeIceTransportPolicy(policy);
    } catch (error) {
      throw new OperationError('change_ice_transport_policy_failed', error);
    }
  }

  public async restartIce(): Promise<void> {
    try {
      const transports = [
        this.#engine.network.sendTransport,
        this.#engine.network.receiveTransport,
        this.#engine.network.signalingTransport,
      ].filter((transport): transport is LDTransport => !!transport);

      await Promise.all(transports.map(async (transport) =>
        await this.#engine.network.restartIce(transport),
      ));
    } catch (error) {
      throw new OperationError('restart_ice_failed', error);
    }
  }

  public transportsStats(): TransportsWebRTCStats | undefined {
    return this.#engine.transportsStats();
  }

  public setSignalingServerUrl(url: string): void {
    this.#engine.setSignalingServerUrl(url);
  }

  public setMeta(meta: Record<string, unknown>): void {
    this.#clientMetaProvider.setExternalMeta(meta);
  }
}

export default Client;
export type { ClientMetaPayload };
